/**
 * cl-core/answer-lead — naver-rank R5 «첫 문단 답 블록» 순수 빌더(2026-10-25).
 *
 *   「{정식명}은 {구} {동} {사업유형}으로 {N}세대 규모이며 시공사는 {시공사}입니다. {기준일} 현재 {단계} 단계입니다.」
 *   분양 현장은 「분양가 X억부터」(바닥 = 최저가 · 가격 원천 있을 때만)와 일정 한 문장을 잇는다.
 *
 * 규칙
 *  - 데이터만 쓴다. 값이 없는 칸은 문장에서 깔끔히 뺀다(자리표시·추정어 금지).
 *  - 형제 구역 이름은 넣지 않는다(R4 — 첫 문단에 형제 이름 금지). 입력이 이 현장 값뿐이다.
 *  - 연도 리터럴 없음(HC-A-03). 기준일 표기는 호출부가 만든 라벨(예: 「2026.10.20 기준」)을 그대로 괄호에 싣는다.
 */
import { topicSubject, josa } from './josa';
import { eokFrom } from './title';

export type AnswerLeadKind = 'redev' | 'sale' | 'existing';

export interface AnswerLeadInput {
  /** 정식명(정비: 「사직2구역 재개발」 또는 「래미안 마크 더 스위트(명륜2구역 재건축)」). */
  name: string;
  kind: AnswerLeadKind;
  sigungu?: string | null;
  dong?: string | null;
  /** 정비 원장 project_type(재개발·재건축). */
  projectType?: string | null;
  totalUnits?: number | null;
  builder?: string | null;
  /** 현재 단계 라벨(스테퍼 「현재」 칸과 같은 말). */
  stage?: string | null;
  /** 기준일 라벨(예: 「2026.10.20 기준」). */
  basisLabel?: string | null;
  priceMinManwon?: number | null;
  priceMaxManwon?: number | null;
  /** 가격 원천 확인(isPriceSourced). false/미지정이면 가격을 싣지 않는다. */
  priceSourced?: boolean;
  /** 일정 한 줄(예: 「청약 접수 10.28~10.30」). 분양 현장만 쓴다. */
  scheduleText?: string | null;
}

function c(s: string | null | undefined): string {
  return String(s ?? '').replace(/\s+/g, ' ').trim();
}

function typeLabel(i: AnswerLeadInput): string {
  if (i.kind === 'redev') {
    const pt = c(i.projectType);
    const w = /재건축/.test(pt) ? '재건축' : /재개발/.test(pt) ? '재개발' : /재건축/.test(i.name) ? '재건축' : '재개발';
    return `${w} 사업`;
  }
  return i.kind === 'existing' ? '아파트 단지' : '분양 단지';
}

/** 답 블록 문장. 이름이 없으면 null. */
export function buildAnswerLead(i: AnswerLeadInput): string | null {
  const name = c(i.name);
  if (!name) return null;
  const sg = c(i.sigungu);
  const dong = c(i.dong);
  const place = [sg, dong && dong !== sg ? dong : ''].filter(Boolean).join(' ');
  const tl = typeLabel(i);
  const units = typeof i.totalUnits === 'number' && Number.isFinite(i.totalUnits) && i.totalUnits > 0 ? `${Math.round(i.totalUnits).toLocaleString('ko-KR')}세대` : '';
  const builder = c(i.builder).replace(/\(주\)|㈜|주식회사/g, '').trim();
  const stage = c(i.stage);
  const basis = c(i.basisLabel);
  /* kw-audit (2026-11-01) — 명사 나열(「1,712세대·대우건설·현재 시공사 선정(기준일).」)을 «완결 문장 둘» 로.
   *   AI 답변·네이버 요약은 문장 단위로 인용한다 — 주어·서술어가 있는 문장이어야 그대로 따 간다.
   *   ① 「{이름}은 {구} {동} {유형}으로 {N}세대 규모이며 시공사는 {시공사}입니다.」
   *   ② (분양가·일정 문장) ③ 「{기준일} 현재 {단계} 단계입니다.」 — 단계 문장을 «맨 끝» 에 둬 청약 상태 문장 합치기(lead-status-merge TAIL)가 이어 받는다. */
  const basisDate = basis.replace(/\s*기준$/, '').trim();
  const stageWord = stage ? (/(단계|중)$/.test(stage) ? stage : `${stage} 단계`) : '';
  const nowLine = stageWord ? `${basisDate ? `${basisDate} ` : ''}현재 ${stageWord}입니다.` : '';
  const from = i.kind === 'sale' && i.priceSourced ? eokFrom(i.priceMinManwon, i.priceMaxManwon) : null;
  const subject = topicSubject(name);
  const what = [place, tl].filter(Boolean).join(' ');
  const lead = `${subject} ${what}`;
  let first: string;
  if (units && builder) first = `${lead}${josa(tl, '으로/로')} ${units} 규모이며 시공사는 ${builder}입니다.`;
  else if (units) first = `${lead}${josa(tl, '으로/로')} ${units} 규모입니다.`;
  else if (builder) first = `${lead}${josa(tl, '으로/로')} 시공사는 ${builder}입니다.`;
  else first = `${lead}입니다.`;
  const extra: string[] = [];
  if (from) extra.push(`분양가는 ${from}입니다.`);
  const sch = i.kind === 'sale' ? c(i.scheduleText) : '';
  if (sch) extra.push(`일정은 ${sch}입니다.`);
  if (nowLine) extra.push(nowLine);
  return [first, ...extra].join(' ');
}
